import { forwardRef, useCallback, useRef, ComponentType } from 'react';
import WebUniversal, { WebUniversalProps } from '../../system/WebUniversal';
import { Button, Tooltip } from 'antd';
import dayjs from 'dayjs';
import fetchApi from '../../../utils/api/fetchApi';
import AntDraggableModal from '../../../utilsTsx/universal/AntDraggableModal';
import { columnsheadId } from '../../../utilsTsx/components/system/columnshead';
import antdMessage from '../../../utilsTsx/components/system/antdMessage';

export interface WxPayRefundOrderBaseConfig {
    /** 业务 apiId，例如 'wxPayRefundOrder' / 'wxPayRefundOrderBak' */
    apiId: string;
    /** 标题（可选，默认“微信退款单”） */
    titleName?: string;
    /** 编辑子组件，由外层注入 */
    WxPayRefundOrderEditComponent: ComponentType<any>;
    /** 透传的外部 props（原函数组件 props） */
    outerProps: any;
}

const WxPayRefundOrderBase = forwardRef<any, WxPayRefundOrderBaseConfig>((config, outerRef) => {
    const {
        apiId,
        titleName = '微信退款单',
        WxPayRefundOrderEditComponent,
        outerProps,
    } = config;
    const { closeModal, editingId } = outerProps;

    /**
     * 内部 ref：给本组件自己用（在 executeWxPayRefund 里读取 defaultService / fetchData）
     * 说明：用 useRef 而非 useState 保存，避免在 ref 变化时 setState 触发重渲染，
     *       从而避免 "Maximum update depth exceeded" 的无限循环。
     */
    const innerRef = useRef<any>(undefined);

    /**
     * 合并 ref：
     * - 写入 innerRef，供本组件内部使用
     * - 同时透传给外部 outerRef，供调用方使用
     * 使用 useCallback 固定引用，避免每次渲染都触发一次 ref 回调。
     */
    const mergedRef = useCallback(
        (node: any) => {
            innerRef.current = node;
            if (typeof outerRef === 'function') {
                outerRef(node);
            } else if (outerRef) {
                outerRef.current = node;
            }
        },
        [outerRef],
    );

    /**
     * 执行微信支付退款
     * @param id 微信退款单id
     */
    const executeWxPayRefund = (id: string) => {
        // 从内部 ref 中读取 WebUniversal 暴露的 handle（无需 useState，避免死循环）
        const handle = innerRef.current;
        if (!handle) return;
        fetchApi.PUT(handle.defaultService, {
            apiId,
            apiExtend: 'execute',
            apiVariable: id
        }).then(res => {
            if (res.success === true) {
                antdMessage('success', '退款成功');
                handle.fetchData?.();
            } else {
                antdMessage('error', '退款失败', { description: res.message });
            }
        });
    };

    /** 表头内容 */
    const columnsHead = [
        {
            title: '微信订单',
            dataIndex: 'wxPayOrderId',
            render: (text: any) => <Tooltip placement="topLeft" title={text}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.length >= 8 ? text.substring(0, 8) + '......' : text)
                    : ''
            }</Tooltip>
        },
        { title: '退款金额', dataIndex: 'refundAmount' },
        { title: '退款状态', dataIndex: 'refundStatus_Name' },
        {
            title: '成功',
            dataIndex: 'isRefund',
            render: (val: boolean) => (val === false ?
                <p style={{ color: 'red' }}>否</p> :
                <p style={{ color: 'yellow' }}>是</p>)
        },
        {
            title: '退款id',
            dataIndex: 'wxPayRefundId',
            render: (text: any) => <Tooltip placement="topLeft" title={text}>{
                (typeof text !== "undefined" && text !== null) ?
                    (text.length >= 8 ? text.substring(0, 8) + '......' : text)
                    : ''
            }</Tooltip>
        },
        {
            title: '退款时间',
            dataIndex: 'refundTime',
            render: (val: dayjs.ManipulateType) => <span>{dayjs(val).format('YYYY-MM-DD HH:MM:ss')}</span>,
        },
        {
            title: '执行',
            dataIndex: 'id',
            render: (text: any) => <Tooltip placement="topLeft" title={'执行退款'}>
                <Button onClick={() => executeWxPayRefund(text)}>退款</Button>
            </Tooltip>
        }
    ];

    const columns = columnsheadId.concat(columnsHead as []);

    let extendProps: WebUniversalProps = {
        ...outerProps,
        titleName,
        serviceId: 'pay',
        apiId,                                  // ← 变量替换硬编码
        columns,
        sendPageData: { wxPayOrderId: editingId },
        displayOptions: {
            showQuery: false
        },
        editOptions: {
            Editor: WxPayRefundOrderEditComponent   // ← 由外部注入
        }
    };

    return (
        <>
            <AntDraggableModal
                title={titleName}
                open={true}
                okText='确定'
                cancelButtonProps={{ style: { display: "none" } }}
                onOk={closeModal}
                closable={false}
                maskClosable={false}
                width={1080}>
                <WebUniversal {...extendProps} ref={mergedRef} />
            </AntDraggableModal>
        </>
    );
});

export default WxPayRefundOrderBase;